import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Button, Card, Form, Table, Alert, Spinner } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function CreateUserPage() {
  const [users, setUsers] = useState([]);
  // Keep a copy of all users so we can toggle between filters
  const [originalUsers, setOriginalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "Users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const role = userDoc.data().role;
          setCurrentUserRole(role);

          if (role !== "admin") {
            setAccessDenied(true); // Restrict access for non-admin users
          }
        }
      }
    };

    const fetchUsersAndRoles = async () => {
      setLoading(true);
      try {
        // Fetch existing users
        const usersSnapshot = await getDocs(collection(db, "Users"));
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
        // Also store the full, original list for later filtering
        setOriginalUsers(usersList);

        // Fetch roles
        const rolesSnapshot = await getDocs(collection(db, "Roles"));
        const rolesList = rolesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRoles(rolesList);
      } catch (error) {
        console.error("Error fetching users or roles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUserRole();
    fetchUsersAndRoles();
  }, []);

  const handleCreateUser = async () => {
    if (!email || !password || !fullName || !role) {
      setFeedback("All fields are required.");
      return;
    }

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Add user details to Firestore
      await setDoc(doc(db, "Users", uid), {
        email,
        fullName,
        role,
        uid,
        createdAt: serverTimestamp(),
      });

      setFeedback("User created successfully!");
      setEmail("");
      setPassword("");
      setFullName("");
      setRole("user");

      // Refresh user list
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const freshUsers = usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(freshUsers);
      setOriginalUsers(freshUsers);
    } catch (error) {
      console.error("Error creating user:", error);
      setFeedback("Failed to create user. Please try again.");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      // Update the user's role to 'deleted'
      const userDocRef = doc(db, "Users", userId);
      await updateDoc(userDocRef, { role: "deleted" });

      setFeedback(`User's role updated to 'deleted'.`);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: "deleted" } : user
        )
      );
      // Also update originalUsers for consistency
      setOriginalUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: "deleted" } : user
        )
      );
    } catch (error) {
      console.error("Error updating user role:", error);
      setFeedback("Failed to update user role. Please try again.");
    }
  };

  // Filter out users who do NOT have the "deleted" role
  const handleFilterDeletedUsers = () => {
    setUsers(originalUsers.filter((user) => user.role !== "deleted"));
  };

  // Show only users who have the "deleted" role
  const handleShowDeletedUsers = () => {
    setUsers(originalUsers.filter((user) => user.role === "deleted"));
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">Only admins can access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-user-add-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Create Users</h1>
          </div>
          <p className="lead text-muted">Create and manage user accounts for your organization</p>
        </div>

        {feedback && (
          <div className="bg-gradient-primary text-white rounded-4 p-4 shadow-lg mb-4">
            <div className="d-flex align-items-center">
              <i className="ri-information-line fs-4 me-3"></i>
              <div>
                <h6 className="mb-0 fw-bold">Notification</h6>
                <p className="mb-0">{feedback}</p>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Create User Form */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-success text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
            <div className="d-flex align-items-center">
              <i className="ri-user-add-line fs-4 me-3"></i>
              <h5 className="mb-0 fw-bold">New User Details</h5>
            </div>
          </div>
          <Card.Body className="p-4">
            <Form>
              <div className="row g-4">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-mail-line me-2"></i>Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                    <Form.Text className="text-muted">
                      <i className="ri-information-line me-1"></i>
                      This will be used for login authentication
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-lock-line me-2"></i>Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                    <Form.Text className="text-muted">
                      <i className="ri-information-line me-1"></i>
                      Minimum 6 characters required
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-user-line me-2"></i>Full Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-shield-user-line me-2"></i>Role
                    </Form.Label>
                    <Form.Select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <div className="text-center mt-4">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={handleCreateUser}
                  className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                  style={{ 
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <i className="ri-user-add-line me-2"></i>Create User
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {/* Enhanced Existing Users Section */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-user-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Existing Users</h5>
              </div>
              <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                {users.length} users
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            {/* Enhanced Filter Buttons */}
            <div className="d-flex gap-3 mb-4">
              <Button 
                variant="primary" 
                onClick={handleFilterDeletedUsers}
                className="px-4 py-2 rounded-pill fw-semibold"
              >
                <i className="ri-eye-line me-2"></i>Show Active Users
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleShowDeletedUsers}
                className="px-4 py-2 rounded-pill fw-semibold"
              >
                <i className="ri-eye-off-line me-2"></i>Show Deleted Users
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" size="lg" />
                <p className="text-secondary mt-3">Loading users...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover table-borderless">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="border-0">
                        <i className="ri-hashtag me-2"></i>#
                      </th>
                      <th className="border-0">
                        <i className="ri-mail-line me-2"></i>Email
                      </th>
                      <th className="border-0">
                        <i className="ri-user-line me-2"></i>User Name
                      </th>
                      <th className="border-0">
                        <i className="ri-shield-user-line me-2"></i>Role
                      </th>
                      <th className="border-0">
                        <i className="ri-calendar-line me-2"></i>Created At
                      </th>
                      <th className="border-0">
                        <i className="ri-settings-3-line me-2"></i>Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id}
                          style={{ 
                            cursor: "pointer",
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'scale(1.01)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}>
                        <td className="fw-semibold">{index + 1}</td>
                        <td className="fw-semibold">{user.email}</td>
                        <td className="fw-semibold">{user.fullName}</td>
                        <td>
                          <span className={`badge rounded-pill px-3 py-2 fw-semibold ${
                            user.role === "admin" 
                              ? "bg-danger" 
                              : user.role === "deleted"
                              ? "bg-secondary"
                              : "bg-success"
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="text-muted">
                          {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="rounded-pill"
                            disabled={user.role === "deleted"}
                          >
                            <i className="ri-delete-bin-line me-1"></i>
                            {user.role === "deleted" ? "Deleted" : "Mark as Deleted"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {users.length === 0 && !loading && (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-user-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Users Found</h5>
                <p className="text-muted">No users match the current filter criteria.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
      <Footer />
    </React.Fragment>
  );
}
